import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/toaster";
import PWAUpdatePrompt from "@/components/PWAUpdatePrompt";

// Layouts
import DashboardLayout from "@/components/layout/DashboardLayout";

// Auth/Onboarding Pages
import SplashScreen from "@/pages/SplashScreen";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VerifyOTPPage from "@/pages/auth/VerifyOTPPage";
import ResetPasswordRequestPage from "@/pages/auth/ResetPasswordRequestPage";
import ResetPasswordConfirmPage from "@/pages/auth/ResetPasswordConfirmPage";

// Dashboard Pages
import DashboardPage from "@/pages/DashboardPage";
import FarmPage from "@/pages/FarmPage";
import DiagnosisPage from "@/pages/DiagnosisPage";
import WeatherPage from "@/pages/WeatherPage";
import MarketplacePage from "@/pages/MarketplacePage";
import ProductsPage from "@/pages/ProductsPage";
import NetworkPage from "@/pages/NetworkPage";
import InventoryPage from "@/pages/InventoryPage";
import DealsPage from "@/pages/DealsPage";
import ProfilePage from "@/pages/ProfilePage";
import NotificationsPage from "@/pages/NotificationsPage";
import PlansPage from "@/pages/subscription/PlansPage";
import SubscriptionSuccessPage from "@/pages/subscription/SuccessPage";
import SubscriptionCancelPage from "@/pages/subscription/CancelPage";
import PaymentsPage from "@/pages/payments/PaymentsPage";
import PaymentReturnPage from "@/pages/payments/PaymentReturnPage";

// New Pages
import EditProfilePage from "@/pages/EditProfilePage";
import CustomersPage from "@/pages/CustomersPage";
import ForecastsPage from "@/pages/ForecastsPage";
import PlannerPage from "@/pages/PlannerPage";
import NutritionPage from "@/pages/NutritionPage";
import SprayPage from "@/pages/SprayPage";
import GovernmentPage from "@/pages/GovernmentPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import LanguagePage from "@/pages/LanguagePage";
import SupportPage from "@/pages/SupportPage";
import VerifyPage from "@/pages/VerifyPage";
import NotFoundPage from "@/pages/NotFoundPage";
import SupportChatPage from "@/pages/SupportChatPage";
import { AdminLoginPage, AdminInvitePage } from "@/pages/admin/AdminAuthPages";
import { AdminShell, AdminChatsPage } from "@/pages/admin/AdminPortal";
import AdminTeamPage from "@/pages/admin/AdminTeamPage";
import AdminMasterListPage from "@/pages/admin/AdminMasterListPage";
import AdminClustersPage from "@/pages/admin/AdminClustersPage";
import DirectoryPage from "@/pages/DirectoryPage";
import WelcomePage from "@/pages/WelcomePage";
import AddProductsPage from "@/pages/AddProductsPage";
import AddShopkeeperPage from "@/pages/AddShopkeeperPage";
import ShopProductsPage from "@/pages/ShopProductsPage";
import SellProducePage from "@/pages/SellProducePage";
import ProduceMarketPage from "@/pages/ProduceMarketPage";
import MyClusterPage from "@/pages/MyClusterPage";
import { ProduceChatsPage, ProduceChatPage } from "@/pages/ProduceChatsPage";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Splash & Onboarding */}
          <Route path="/" element={<SplashScreen />} />
          {/* The onboarding slides were replaced by the intro video on "/". */}
          <Route path="/onboarding" element={<Navigate to="/" replace />} />
          
          {/* Auth Routes (no layout wrapper) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-otp" element={<VerifyOTPPage />} />
          {/* Password reset — Twilio-OTP flow mirroring signup (PR 14). */}
          <Route path="/forgot-password" element={<ResetPasswordRequestPage />} />
          <Route path="/forgot-password/verify" element={<ResetPasswordConfirmPage />} />

          {/* Support portal for the African Farm team (own sign-in). */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/invite" element={<AdminInvitePage />} />
          <Route element={<AdminShell />}>
            <Route path="/admin/chats" element={<AdminChatsPage />} />
            <Route path="/admin/chats/:threadId" element={<AdminChatsPage />} />
            <Route path="/admin/team" element={<AdminTeamPage />} />
            <Route path="/admin/products" element={<AdminMasterListPage />} />
            <Route path="/admin/clusters" element={<AdminClustersPage />} />
          </Route>

          {/* First-time wizard: full screen, before the dashboard */}
          <Route path="/welcome" element={<WelcomePage />} />

          {/* Protected Routes */}
          <Route element={<DashboardLayout />}>
            {/* Core Pages */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/edit" element={<EditProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            {/* Chats between users were removed (PR 56); the header's
                Support button opens the chat with the support team. */}
            <Route path="/support/chat" element={<SupportChatPage />} />
            <Route path="/messages/*" element={<Navigate to="/support/chat" replace />} />
            <Route path="/subscription" element={<Navigate to="/subscription/plans" replace />} />
            <Route path="/subscription/plans" element={<PlansPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/payments/return" element={<PaymentReturnPage />} />
            {/* /cart was removed in PR 7 — the platform never persisted
                orders. The farmer flow is now "Ask my shopkeeper" from
                a product page, which fans the request out as a
                notification. Any legacy deep link lands on the
                marketplace. */}
            <Route path="/cart" element={<Navigate to="/marketplace" replace />} />

            {/* Farmer Pages */}
            <Route path="/farm" element={<FarmPage />} />
            <Route path="/diagnosis" element={<DiagnosisPage />} />
            <Route path="/weather" element={<WeatherPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/planner" element={<PlannerPage />} />
            <Route path="/nutrition" element={<NutritionPage />} />
            <Route path="/spray" element={<SprayPage />} />
            <Route path="/government" element={<GovernmentPage />} />
            {/* Verify Products — Coming Soon (PR 9). All four role homes have
                a tile linking here; prior to PR 9 no route existed and the
                catch-all sent users to the splash screen. */}
            <Route path="/verify" element={<VerifyPage />} />
            
            {/* Manufacturer Pages */}
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            
            {/* Distributor Pages */}
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/my-products/add" element={<AddProductsPage />} />
            <Route path="/shopkeepers/add" element={<AddShopkeeperPage />} />
            <Route path="/shopkeepers/:shopId/products" element={<ShopProductsPage />} />
            <Route path="/sell" element={<SellProducePage />} />
            <Route path="/produce" element={<ProduceMarketPage />} />
            <Route path="/cluster" element={<MyClusterPage />} />
            <Route path="/produce/chats" element={<ProduceChatsPage />} />
            <Route path="/produce/chats/:chatId" element={<ProduceChatPage />} />
            <Route path="/forecasts" element={<ForecastsPage />} />
            
            {/* Shopkeeper Pages */}
            <Route path="/customers" element={<CustomersPage />} />
            {/* Directory of farmers to invite as customers. */}
            <Route path="/customers/find" element={<NetworkPage type="customers" />} />
            
            {/* Shared Pages */}
            {/* /orders was removed in PR 17 — the platform has no
                orders flow on any role. Legacy deep links from
                browser history, saved shortcuts, or old emails are
                redirected to each role's primary workflow instead
                of 404-ing. */}
            <Route path="/orders" element={<Navigate to="/dashboard" replace />} />
            <Route path="/orders/new" element={<Navigate to="/dashboard" replace />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/distributors" element={<NetworkPage type="distributors" />} />
            <Route path="/suppliers" element={<NetworkPage type="suppliers" />} />
            <Route path="/shopkeepers" element={<NetworkPage type="shopkeepers" />} />
            {/* Who is on African Farm, by type — the photo circles on every home. */}
            <Route path="/directory/:type" element={<DirectoryPage />} />
            
            {/* Settings Pages */}
            <Route path="/language" element={<LanguagePage />} />
            <Route path="/support" element={<SupportPage />} />
          </Route>

          {/* Fallback — real 404, not a redirect back to splash (which hid
              typos / stale links behind the welcome animation). */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
      <PWAUpdatePrompt />
    </AuthProvider>
  );
}

export default App;
